<template>
  <section class="export-panel">
    <!-- Left: Selection Editor - Only show when there's an active selection -->
    <div class="selection-section" v-if="hasSelection">
      <div class="selection-header">
        <span class="section-label">
          {{ editingCutId ? 'Edit Cut #' + editingCutIndex : 'New Selection' }}
        </span>
      </div>
      <div class="selection-controls">
        <div class="time-group">
          <label>Start</label>
          <div class="time-row">
            <button class="nudge-btn" @click="nudge('start', -1000)">−1s</button>
            <input type="datetime-local" step="1" :value="startFormatted" @change="updateStart">
            <button class="nudge-btn" @click="nudge('start', 1000)">+1s</button>
            <button class="preview-btn" :class="{ playing: previewType === 'start' }" @click="previewStart">
              <AppIcon name="play" />
            </button>
          </div>
        </div>
        <div class="time-group">
          <label>End</label>
          <div class="time-row">
            <button class="nudge-btn" @click="nudge('end', -1000)">−1s</button>
            <input type="datetime-local" step="1" :value="endFormatted" @change="updateEnd">
            <button class="nudge-btn" @click="nudge('end', 1000)">+1s</button>
            <button class="preview-btn" :class="{ playing: previewType === 'end' }" @click="previewEnd">
              <AppIcon name="play" />
            </button>
          </div>
        </div>
        <div class="duration-display">
          <label>Duration</label>
          <span class="duration-value">{{ durationFormatted }}</span>
        </div>
        <div class="selection-actions">
          <button class="btn-center" @click="centerView" title="Center view on selection">
            <AppIcon name="crosshairs" />
          </button>
          <template v-if="editingCutId">
            <button class="btn-update" :disabled="!canUpdateCut" @click="updateCut">
              <AppIcon name="check" />Update
            </button>
          </template>
          <template v-else>
            <button class="btn-add" :disabled="!canAddCurrentCut" @click="addCut">
              <AppIcon name="plus" />Add Cut
            </button>
          </template>
          <button class="btn-clear" @click="clearSelection" title="Clear selection">
            <AppIcon name="times" />
          </button>
        </div>
      </div>
    </div>

    <!-- Placeholder when no selection -->
    <div class="selection-placeholder" v-else>
      <AppIcon name="mouse-pointer" />
      <span>Select a range on the timeline or click a cut to edit</span>
    </div>

    <!-- Right: Queue + Export -->
    <div class="queue-export-section">
      <!-- Queue -->
      <div class="queue-section" :class="{ 'has-cuts': cutsCount > 0 }">
        <div class="queue-header">
          <span class="section-label">
            Export Queue
            <span class="queue-badge" v-if="cutsCount > 0">{{ cutsCount }}</span>
          </span>
          <span class="queue-total" v-if="cutsCount > 0">Total: {{ totalDurationFormatted }} / 4:00:00</span>
        </div>
        
        <div class="cuts-list" v-if="cutsCount > 0">
          <div 
            v-for="(cut, index) in exportCuts" 
            :key="cut.id" 
            class="cut-item"
            :class="{ 'editing': editingCutId === cut.id }"
            @click="toggleEditCut(cut, index)"
          >
            <span class="cut-number">{{ index + 1 }}</span>
            <span class="cut-times">{{ formatCutTime(cut.start) }} → {{ formatCutTime(cut.end) }}</span>
            <span class="cut-duration">({{ formatCutDuration(cut) }})</span>
            <div class="cut-actions" @click.stop>
              <button 
                class="cut-btn" 
                @click="moveCut(index, -1)" 
                :disabled="index === 0"
                title="Move up"
              >
                <AppIcon name="chevron-up" />
              </button>
              <button 
                class="cut-btn" 
                @click="moveCut(index, 1)" 
                :disabled="index === cutsCount - 1"
                title="Move down"
              >
                <AppIcon name="chevron-down" />
              </button>
              <button class="cut-btn cut-btn-remove" @click="removeCut(cut.id)" title="Remove">
                <AppIcon name="trash" />
              </button>
            </div>
          </div>
        </div>
        
        <div class="queue-empty" v-else>
          <span>No cuts in queue</span>
        </div>
      </div>

      <!-- Export -->
      <div class="export-section">
        <input 
          type="text" 
          v-model="exportTitle" 
          placeholder="Title (optional)" 
          class="title-input"
        >
        <div v-if="customFieldDefs.length > 0" class="custom-fields-row">
          <div v-for="field in customFieldDefs" :key="field._id" class="custom-field-input">
            <template v-if="field.type === 'text'">
              <input 
                type="text" 
                v-model="customFieldValues[field.key]" 
                :placeholder="field.name + (field.required ? ' *' : '')"
                class="title-input"
                :class="{ 'required-empty': field.required && cutsCount > 0 && !customFieldValues[field.key] }"
              >
            </template>
            <template v-else-if="field.type === 'number'">
              <input 
                type="number" 
                v-model.number="customFieldValues[field.key]" 
                :placeholder="field.name + (field.required ? ' *' : '')"
                class="title-input"
                :class="{ 'required-empty': field.required && cutsCount > 0 && (customFieldValues[field.key] === undefined || customFieldValues[field.key] === null || customFieldValues[field.key] === '') }"
              >
            </template>
            <template v-else-if="field.type === 'dropdown'">
              <select 
                v-model="customFieldValues[field.key]" 
                class="title-input"
                :class="{ 'required-empty': field.required && cutsCount > 0 && !customFieldValues[field.key] }"
              >
                <option value="">{{ field.name }}{{ field.required ? ' *' : '' }}</option>
                <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </template>
            <template v-else-if="field.type === 'boolean'">
              <label class="bool-field">
                <input type="checkbox" v-model="customFieldValues[field.key]">
                <span>{{ field.name }}</span>
              </label>
            </template>
          </div>
        </div>
        <div class="export-buttons">
          <button class="btn-clear-queue" v-if="cutsCount > 0" @click="clearAllCuts" title="Clear queue">
            <AppIcon name="trash" />
          </button>
          <button 
            class="btn-export" 
            :disabled="!canExport || exporting" 
            @click="doExport"
            :title="missingRequiredFields.length > 0 ? 'Required: ' + missingRequiredFields.join(', ') : ''"
          >
            <AppIcon :name="exporting ? 'spinner' : 'download'" :spin="exporting && true" />
            {{ exportButtonText }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="exportMessage" class="export-message">
      <AppIcon name="times-circle" />
      {{ exportMessage }}
    </div>
    
    <!-- Validation Modal -->
    <ConfirmModal
      :visible="showValidationModal"
      :title="validationTitle"
      :message="validationMessage"
      type="warning"
      confirmText="OK"
      :showCancel="false"
      @confirm="showValidationModal = false"
    />
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import audioController from '../services/audioController'
import { startMultiCutExport, getConfig, getCustomFields } from '../services/api'
import { formatDatetimeLocal } from '../services/helpers'
import ConfirmModal from './ConfirmModal.vue'

export default {
  name: 'ExportPanel',
  components: { ConfirmModal },
  data() {
    return {
      exportTitle: '',
      exporting: false,
      exportMessage: '',
      showValidationModal: false,
      validationTitle: '',
      validationMessage: '',
      editingCutId: null,
      editingCutIndex: null,
      maxExportDuration: 2 * 60 * 60 * 1000, // Default 2 hours
      maxMultiExportDuration: 4 * 60 * 60 * 1000, // Default 4 hours
      customFieldDefs: [],
      customFieldValues: {}
    }
  },
  async created() {
    try {
      const config = await getConfig()
      this.maxExportDuration = config.maxExportDuration
      this.maxMultiExportDuration = config.maxMultiExportDuration
    } catch (err) {
      console.warn('Failed to fetch config, using defaults:', err.message)
    }
    try {
      const fields = await getCustomFields()
      this.customFieldDefs = (fields || []).filter(f => f.appliesTo && f.appliesTo.includes('exports'))
    } catch (err) {
      console.warn('Failed to fetch custom fields:', err.message)
    }
  },
  computed: {
    ...mapState(['selectionStart', 'selectionEnd', 'previewType', 'viewDuration', 'exportCuts']),
    ...mapGetters([
      'selectionNormalized', 
      'selectionDurationFormatted', 
      'selectionDuration',
      'exportCutsCount', 
      'exportCutsTotalDuration',
      'exportCutsTotalDurationFormatted',
      'canAddCut'
    ]),
    hasSelection() {
      return this.selectionStart !== null && this.selectionEnd !== null
    },
    startFormatted() { 
      return this.selectionNormalized ? formatDatetimeLocal(this.selectionNormalized.start) : '' 
    },
    endFormatted() { 
      return this.selectionNormalized ? formatDatetimeLocal(this.selectionNormalized.end) : '' 
    },
    durationFormatted() { 
      return this.selectionDurationFormatted 
    },
    cutsCount() {
      return this.exportCutsCount
    },
    totalDurationFormatted() {
      return this.exportCutsTotalDurationFormatted
    },
    canAddCurrentCut() {
      if (!this.selectionNormalized) return false
      if (this.editingCutId) return false // Can't add while editing
      const duration = this.selectionDuration
      if (duration < 1000) return false
      // Check if adding this cut would exceed max total duration
      if (this.exportCutsTotalDuration + duration > this.maxMultiExportDuration) return false
      // Check if single cut exceeds max single export duration
      if (duration > this.maxExportDuration) return false
      return true
    },
    canUpdateCut() {
      if (!this.selectionNormalized) return false
      if (!this.editingCutId) return false
      const duration = this.selectionDuration
      if (duration < 1000) return false
      return true
    },
    canExport() {
      // Only allow export when there are cuts in the queue and required custom fields are filled
      return this.cutsCount > 0 && this.customFieldsValid
    },
    customFieldsValid() {
      for (const field of this.customFieldDefs) {
        if (field.required) {
          const val = this.customFieldValues[field.key]
          if (val === undefined || val === null || val === '') {
            return false
          }
        }
      }
      return true
    },
    missingRequiredFields() {
      return this.customFieldDefs
        .filter(f => f.required)
        .filter(f => {
          const val = this.customFieldValues[f.key]
          return val === undefined || val === null || val === ''
        })
        .map(f => f.name)
    },
    exportButtonText() {
      if (this.exporting) return 'Exporting...'
      if (this.cutsCount > 0) return `Export ${this.cutsCount} Cut${this.cutsCount > 1 ? 's' : ''}`
      return 'Export'
    }
  },
  methods: {
    nudge(target, delta) { 
      this.$store.dispatch('nudgeSelection', { target, delta }) 
    },
    updateStart(e) {
      const t = new Date(e.target.value).getTime()
      if (!isNaN(t) && this.selectionNormalized) {
        this.$store.commit('SET_SELECTION', { start: t, end: this.selectionNormalized.end })
      }
    },
    updateEnd(e) {
      const t = new Date(e.target.value).getTime()
      if (!isNaN(t) && this.selectionNormalized) {
        this.$store.commit('SET_SELECTION', { start: this.selectionNormalized.start, end: t })
      }
    },
    previewStart() { audioController.previewStart() },
    previewEnd() { audioController.previewEnd() },
    clearSelection() { 
      this.$store.dispatch('clearSelection')
      this.editingCutId = null
      this.editingCutIndex = null
      this.exportMessage = ''
    },
    addCut() {
      if (!this.canAddCurrentCut) return
      
      const sel = this.selectionNormalized
      const duration = sel.end - sel.start
      
      if (duration < 1000) {
        this.showValidation('Selection Too Short', 'Please select at least 1 second.')
        return
      }
      
      // Check single cut duration
      if (duration > this.maxExportDuration) {
        const maxMinutes = Math.floor(this.maxExportDuration / 60000)
        this.showValidation('Selection Too Long', `Single export cannot exceed ${maxMinutes} minutes.`)
        return
      }
      
      const newTotal = this.exportCutsTotalDuration + duration
      if (newTotal > this.maxMultiExportDuration) {
        const maxHours = Math.floor(this.maxMultiExportDuration / 3600000)
        this.showValidation('Total Duration Exceeded', `Total export duration cannot exceed ${maxHours} hours.`)
        return
      }
      
      this.$store.commit('ADD_EXPORT_CUT', { start: sel.start, end: sel.end })
      // Clear selection to hide controls - user must select new range or click a cut to edit
      this.$store.dispatch('clearSelection')
    },
    toggleEditCut(cut, index) {
      // If clicking the same cut that's being edited, uncheck it and hide selection
      if (this.editingCutId === cut.id) {
        this.editingCutId = null
        this.editingCutIndex = null
        this.$store.dispatch('clearSelection')
        return
      }
      // Otherwise, edit this cut
      this.editCut(cut, index)
    },
    editCut(cut, index) {
      // Load cut into selection for editing
      this.editingCutId = cut.id
      this.editingCutIndex = index + 1
      this.$store.commit('SET_SELECTION', { start: cut.start, end: cut.end })
      this.$store.commit('SET_SELECTION_MODE', true)
    },
    centerView() {
      const sel = this.selectionNormalized
      if (!sel) return
      const mid = (sel.start + sel.end) / 2
      
      // Update currentTime to the center of selection so play starts from here
      this.$store.commit('SET_LIVE', false)
      this.$store.commit('SET_VIEW_LOCKED', false)
      this.$store.commit('SET_CURRENT_TIME', mid)
      this.$store.commit('SET_CURRENT_SEGMENT_ID', null)
    },
    updateCut() {
      if (!this.canUpdateCut || !this.editingCutId) return
      
      const sel = this.selectionNormalized
      const duration = sel.end - sel.start
      
      // Calculate new total (excluding the cut being edited)
      const otherCutsDuration = this.exportCuts
        .filter(c => c.id !== this.editingCutId)
        .reduce((sum, c) => sum + (c.end - c.start), 0)
      
      // Check single cut duration
      if (duration > this.maxExportDuration) {
        const maxMinutes = Math.floor(this.maxExportDuration / 60000)
        this.showValidation('Selection Too Long', `Single export cannot exceed ${maxMinutes} minutes.`)
        return
      }
      
      if (otherCutsDuration + duration > this.maxMultiExportDuration) {
        const maxHours = Math.floor(this.maxMultiExportDuration / 3600000)
        this.showValidation('Total Duration Exceeded', `Total export duration cannot exceed ${maxHours} hours.`)
        return
      }
      
      this.$store.commit('UPDATE_EXPORT_CUT', { 
        id: this.editingCutId, 
        start: sel.start, 
        end: sel.end 
      })
      
      // Clear edit mode and selection after update
      this.editingCutId = null
      this.editingCutIndex = null
      this.$store.dispatch('clearSelection')
    },
    cancelEdit() {
      // Cancel edit mode and clear selection
      this.editingCutId = null
      this.editingCutIndex = null
      this.$store.dispatch('clearSelection')
    },
    removeCut(cutId) {
      if (this.editingCutId === cutId) {
        this.editingCutId = null
        this.editingCutIndex = null
        this.$store.dispatch('clearSelection')
      }
      this.$store.commit('REMOVE_EXPORT_CUT', cutId)
    },
    clearAllCuts() {
      this.$store.commit('CLEAR_EXPORT_CUTS')
      this.editingCutId = null
      this.editingCutIndex = null
      this.$store.dispatch('clearSelection')
      this.exportTitle = ''
    },
    moveCut(index, direction) {
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= this.cutsCount) return
      this.$store.commit('REORDER_EXPORT_CUTS', { fromIndex: index, toIndex: newIndex })
    },
    formatCutTime(timestamp) {
      const d = new Date(timestamp)
      return d.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit',
        hour12: false 
      })
    },
    formatCutDuration(cut) {
      const ms = cut.end - cut.start
      const minutes = Math.floor(ms / 60000)
      const seconds = Math.floor((ms % 60000) / 1000)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    showValidation(title, message) {
      this.validationTitle = title
      this.validationMessage = message
      this.showValidationModal = true
    },
    showMessage(message) {
      this.exportMessage = message
      setTimeout(() => { this.exportMessage = '' }, 5000)
    },
    async doExport() {
      if (this.exporting || !this.canExport) return
      
      // Validate custom fields
      const validationErrors = []
      for (const field of this.customFieldDefs) {
        const val = this.customFieldValues[field.key]
        if (field.required && (val === undefined || val === null || val === '')) {
          validationErrors.push(`${field.name} is required`)
          continue
        }
        if (val !== undefined && val !== null && val !== '') {
          if (field.type === 'number' && typeof val !== 'number' && isNaN(Number(val))) {
            validationErrors.push(`${field.name} must be a number`)
          }
          if (field.type === 'dropdown' && field.options && field.options.length > 0 && !field.options.includes(val)) {
            validationErrors.push(`${field.name} must be one of: ${field.options.join(', ')}`)
          }
        }
      }
      if (validationErrors.length > 0) {
        this.validationTitle = 'Custom Field Errors'
        this.validationMessage = validationErrors.join('\n')
        this.showValidationModal = true
        return
      }
      
      this.exporting = true
      
      try {
        const cuts = this.exportCuts.map(c => ({ start: c.start, end: c.end }))
        // Build customFields from values (only include non-empty)
        const customFields = {}
        for (const field of this.customFieldDefs) {
          const val = this.customFieldValues[field.key]
          if (val !== undefined && val !== null && val !== '') {
            customFields[field.key] = val
          }
        }
        await startMultiCutExport(cuts, this.exportTitle.trim(), customFields)
        this.$emit('export-started')
        this.$store.commit('CLEAR_EXPORT_CUTS')
        this.$store.dispatch('clearSelection')
        this.editingCutId = null
        this.editingCutIndex = null
        this.exportTitle = ''
        this.customFieldValues = {}
      } catch (err) {
        this.showMessage('Export failed: ' + (err.message || 'Unknown error'))
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.export-panel {
  background: var(--white);
  border-bottom: 1px solid var(--gray-200);
  padding: 10px 16px;
  display: flex;
  align-items: stretch;
  gap: 16px;
  flex-shrink: 0;
}

.section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--gray-500);
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Selection Section - Left */
.selection-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.selection-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-cancel-edit {
  font-size: 12px;
  padding: 4px 10px;
  background: var(--gray-100);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-cancel-edit:hover {
  background: var(--gray-200);
}

.selection-controls {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.time-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.time-group label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--gray-500);
}

.time-row {
  display: flex;
  align-items: center;
  gap: 2px;
}

.time-group input {
  padding: 6px 8px;
  font-size: 13px;
  font-family: 'Poppins', sans-serif;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-700);
  width: 155px;
}

.nudge-btn {
  padding: 6px 8px;
  font-size: 12px;
  background: var(--gray-100);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
}

.nudge-btn:hover { background: var(--gray-200); }

.preview-btn {
  padding: 6px 10px;
  font-size: 11px;
  background: var(--gray-800);
  border: none;
  border-radius: 4px;
  color: var(--white);
  cursor: pointer;
}

.preview-btn:hover { background: var(--gray-700); }
.preview-btn.playing { background: #262626; }

.duration-display {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.duration-display label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--gray-500);
}

.duration-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--gray-700);
  padding: 3px 0;
}

.selection-actions {
  display: flex;
  gap: 4px;
  align-items: flex-end;
}

.btn-add, .btn-update {
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-add {
  background: #10b981;
}

.btn-add:hover:not(:disabled) { background: #059669; }

.btn-update {
  background: #3b82f6;
}

.btn-update:hover:not(:disabled) { background: #2563eb; }

.btn-add:disabled, .btn-update:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-center {
  padding: 8px 12px;
  font-size: 13px;
  background: var(--gray-100);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
}

.btn-center:hover { background: var(--gray-200); color: var(--gray-800); }

.btn-clear {
  padding: 8px 12px;
  font-size: 13px;
  background: var(--gray-100);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-500);
  cursor: pointer;
}

.btn-clear:hover { background: var(--gray-200); color: var(--gray-700); }

/* Selection Placeholder */
.selection-placeholder {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  color: var(--gray-400);
  font-size: 12px;
}

.selection-placeholder svg {
  font-size: 16px;
}

/* Queue + Export Section - Right */
.queue-export-section {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.queue-section {
  min-width: 340px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
  background: var(--gray-50);
  border-radius: 6px;
  border: 1px dashed var(--gray-200);
}

.queue-section.has-cuts {
  border-style: solid;
  background: white;
}

.queue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.queue-badge {
  background: var(--gray-800);
  color: white;
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 8px;
}

.queue-total {
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-600);
}

.cuts-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cut-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: var(--gray-50);
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
  border: 1px solid transparent;
}

.cut-item:hover {
  background: var(--gray-100);
}

.cut-item.editing {
  background: #dbeafe;
  border-color: #3b82f6;
}

.cut-number {
  width: 20px;
  height: 20px;
  background: var(--gray-700);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.cut-item.editing .cut-number {
  background: #3b82f6;
}

.cut-times {
  font-family: 'SF Mono', 'Monaco', monospace;
  color: var(--gray-700);
  white-space: nowrap;
}

.cut-duration {
  color: var(--gray-500);
  white-space: nowrap;
}

.cut-actions {
  display: flex;
  gap: 3px;
  margin-left: auto;
}

.cut-btn {
  padding: 4px 8px;
  font-size: 11px;
  background: var(--gray-200);
  border: none;
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
}

.cut-btn:hover:not(:disabled) { background: var(--gray-300); }
.cut-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.cut-btn-remove:hover { background: #fee2e2; color: #dc2626; }

.queue-empty {
  padding: 10px 0;
  color: var(--gray-500);
  font-size: 12px;
  text-align: center;
}

/* Export Section */
.export-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
}

.title-input {
  padding: 6px 10px;
  font-size: 11px;
  font-family: 'Poppins', sans-serif;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  width: 140px;
}

.title-input:focus { outline: none; border-color: var(--gray-400); }
.title-input::placeholder { color: var(--gray-400); }
.title-input.required-empty { border-color: #fca5a5; }
.title-input.required-empty::placeholder { color: #f87171; }

.export-buttons {
  display: flex;
  gap: 6px;
}

.btn-clear-queue {
  padding: 6px 10px;
  font-size: 11px;
  background: var(--gray-100);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-500);
  cursor: pointer;
}

.btn-clear-queue:hover {
  background: #fee2e2;
  border-color: #fecaca;
  color: #dc2626;
}

.btn-export {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  background: #262626;
  color: var(--white);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-export:hover:not(:disabled) { background: #1a1a1a; }
.btn-export:disabled { opacity: 0.5; cursor: not-allowed; }

.export-message {
  position: absolute;
  bottom: -28px;
  left: 16px;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 5px;
  background: #fef2f2;
  color: #dc2626;
}

/* Custom Fields in Export Section */
.custom-fields-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.custom-field-input {
  flex: 0 0 auto;
}

.custom-field-input select.title-input {
  width: 140px;
  appearance: auto;
  cursor: pointer;
}

.bool-field {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--gray-600);
  cursor: pointer;
  user-select: none;
  padding: 4px 0;
}

.bool-field input[type="checkbox"] {
  width: 14px;
  height: 14px;
  accent-color: var(--accent);
  cursor: pointer;
}
</style>
